/**
 * In-flight request coalescing (single-flight).
 *
 * Concurrent callers asking for the same `key` while a request is already in
 * flight share one promise — and therefore one underlying network request.
 * Once the request settles the key is released, so the next call (e.g. a
 * refresh triggered after an edit) always issues a fresh request and never
 * serves stale data.
 *
 * Generalises the shared-refresh-promise pattern already used in api.ts for 401s.
 */
const inFlight = new Map<string, Promise<unknown>>();

export function coalesceRequest<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = run().finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);
  return promise;
}
